import React, { useState, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import ChatRoomWindow from 'components/Chat/ChatWindow/ChatRoomWindow';
import CPP from 'components/Patient/CPP';
import './style.scss';
import PatientInfoRow from 'components/Patient/PatientInfoRow';
import AddressSidePanel from 'components/HealthRecords/Appointments/AddressSidePanel';
import ResourcesPanel from './ResourcesPanel';
import Icon from 'components/Utils/NewIcon';
import RouteUtils from 'services/RouteUtils';
import DefaultTextField from 'components/Utils/DefaultTextField';
import SplitPanel from 'components/Utils/SplitPanel';
import CPPSplitPanel from 'components/Patient/CPPChat/CPPSplitPanel';
import AppointmentUtils from 'services/AppointmentUtils';
import { STATUS } from 'scenes/HomeStaff/ClinicQueue/QueueBadge';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { videoActions } from 'careand-redux/slices/ui/video';
import { uiActions } from 'careand-redux/slices/ui/ui';
import { patientActions } from 'careand-redux/slices/patients';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import { GetStaff, IsAdmin, IsTypePhlebotomist } from 'careand-redux/selectors';
import ChatBubbleUtils from 'services/ChatBubbleUtils';
import PhysicianTopBar from 'components/Chat/TopBars/PhysicianTopBar';
import usePersistedState from 'hooks/usePersistedState';
import { useMediaQuery } from '@material-ui/core';
import PatientUtils from 'services/PatientUtils';
import { useSpotlightPatientContext } from 'services/Spotlight/SpotlightAIContext';
import { usePathParams } from 'components/Navigation/NavStack';
import { TRANSCRIPTION_STATUS } from 'services/DictationService/TelemedicineTranscriptionService';
import CPPContext from '../CPP/CPPContext';
import { Device } from '../../../services/Native/Device';

const CPPChat = (props) => {
  const { bubble, defaultHidePanel } = props;

  const selectedBubbleId = usePathParams()?.bubbleId;
  const isPhlebotomist = useAppSelector(IsTypePhlebotomist);
  const isAdmin = useAppSelector(IsAdmin);
  const patients = useAppSelector((state) => state.patients.byId);
  const bubbles = useAppSelector((state) => state.ui.bubbles);

  const ownUser = useAppSelector(GetStaff);
  const physicianIds = [ownUser.id, ownUser.intern_of];

  const nav = useNavStack(Stacks.Master);
  const dispatch = useAppDispatch();

  const unreactiveNoteValue = useRef(undefined);
  const cppContextState = useMemo(() => ({ unreactiveNoteValue }), [unreactiveNoteValue]);

  const patientId = bubble ? bubble.patient_id : props.patient.id;

  useSpotlightPatientContext(patientId);

  useEffect(() => {
    dispatch(uiActions.setActiveBubbleId(selectedBubbleId));
  }, [selectedBubbleId]);

  const selectedBubble = useMemo(() => {
    const patientBubbles = bubbles.filter(({ patient_id }) => patient_id === patientId);
    if (selectedBubbleId) {
      return patientBubbles.find((it) => it.id === selectedBubbleId);
    }
    return (
      _.sortBy(patientBubbles, 'date')
        .reverse()
        .find(({ appointment_id }) => !!appointment_id) || patientBubbles[0]
    );
  }, [patientId, bubbles, selectedBubbleId]);

  const patientBubble = bubble || selectedBubble;

  const patient = patientBubble ? patients[patientId] : props.patient;

  const activeAppointments = useAppSelector((state) => state.appointments.activeAppointments);
  const pinnedDocuments = useAppSelector((state) => state.ui.pinned[patientId]);
  const queue = useAppSelector((state) => state.queue.elements);

  const [showChat, setShowChat] = useState(patientBubble?.chat);
  const [showAddress, setShowAddress] = useState(false);
  const [showResources, setShowResources] = useState(false);
  const [loadingChatIcon, setLoadingChatIcon] = useState(false);
  const [search, setSearch] = useState('');
  const [defaultSize, setDefaultSize] = usePersistedState('cpp.splitSize2', '35%');
  const isPhone = useMediaQuery('(max-width: 767px)');
  const [showUserIcons, setShowUserIcons] = useState(false);

  const appointment = activeAppointments[patientBubble?.appointment_id];

  const allowClinicTranscription = appointment && physicianIds.includes(appointment?.physician_id);

  const roomName = patientBubble?.room_name;

  useEffect(() => {
    if (!!roomName) setShowChat(true);
  }, [roomName]);

  useEffect(() => {
    dispatch(patientActions.patientOpened(patientId));
  }, [patientId, dispatch]);

  const mainPatient = patient;

  const queueElement = useMemo(() => {
    return queue.find(({ patient_id }) => patient_id === patient.id);
  }, [patient, queue]);

  if (patient === undefined) return null;

  let isPanelOpen = showChat || showResources || showAddress;

  const queueElementStatus = queueElement?.status;

  let className = 'chat-cpp';
  let chatClassNames = 'chat-cpp-chat-section' + (isPanelOpen ? '' : 'hide');

  async function startVideoCall(appointmentId) {
    if (Device.isMobile()) {
      const meetingData = await dispatch(
        api.Video.joinVideoAppointment2({ appointmentId: appointmentId }),
        { errorTitle: 'Join Appointment' },
      );
      if (meetingData) {
        Device.showTelemedicine(meetingData);
      }
      return;
    }

    dispatch(videoActions.openVideoScreen(undefined));
    let videoBubble = patientBubble;
    if (!videoBubble || patientId !== patient.id) {
      videoBubble = {
        id: patientId,
        patient_id: patientId,
      };
    }
    setTimeout(() => {
      dispatch(
        videoActions.openVideoScreen({
          ...videoBubble,
          appointment_id: appointmentId,
          saveTo: patientId,
        }),
      );
      startQueueAppointment();
    });
  }

  function toggleResources(val) {
    setShowResources(val);
    setShowAddress(false);
    setShowChat(false);
  }

  function toggleAddress(val) {
    setShowAddress(val);
    setShowResources(false);
    setShowChat(false);
  }

  function closeChat() {
    setShowChat(false);
    setShowResources(false);
  }

  function findBubble() {
    return bubbles.find((bubble) => bubble.patient_id === patientId && bubble.chat);
  }

  function setActiveChatBubble(bubbleId) {
    let route = RouteUtils.getRoute(RouteUtils.routes().bubble, {
      ':bubbleId': bubbleId,
    });
    nav.push(route);
  }

  async function openChat(value, mainPatient) {
    let bubble = patientBubble;
    if (!value) {
      setShowChat(false);
      setShowResources(false);
    } else if (bubble?.room_name) {
      setShowChat(true);
      setShowResources(false);
    } else {
      let existingChatBubble = bubbles.find(
        (b) => b.patient_id === patientId && b.chat && b.room_name,
      );
      if (existingChatBubble) {
        setActiveChatBubble(existingChatBubble.id);
      } else {
        setLoadingChatIcon(true);
        let options = { errorTitle: 'Create Chat' };
        let chat = await dispatch(
          api.Chat.createOnDemandChatRoom({ patientId: mainPatient }),
          options,
        );
        if (chat) {
          let newBubble = ChatBubbleUtils.chatBubbleFromChatRoom(chat, activeAppointments);
          if (newBubble) {
            if (bubble) {
              // Merge room_name into existing appointment bubble before showing chat
              let updatedBubbles = bubbles.map((b) =>
                b.id === bubble.id
                  ? { ...b, room_name: newBubble.id, chat: true, isLiveChat: true }
                  : b,
              );
              dispatch(uiActions.setBubbles(updatedBubbles));
              setShowChat(true);
              setShowResources(false);
            } else {
              // No existing bubble — add the new one and navigate to it
              dispatch(uiActions.setBubbles([...bubbles, newBubble]));
              setActiveChatBubble(newBubble.id);
            }
          }
        }
        setLoadingChatIcon(false);
      }
    }
  }

  async function startQueueAppointment() {
    // Only telemedicine (video/phone) appointments use the 'video' room
    // sentinel to flag a remote start. Stamping it on an in-person clinic
    // appointment makes the server tell the patient to "proceed to video".
    if (queueElementStatus === STATUS.waiting && isTelemedicine) {
      let element = {
        ...queueElement,
        status: STATUS.called_in,
        room: 'video',
      };
      dispatch(api.Queue.updateQueueElement({ id: queueElement.id, queueElement: element }));
    }
  }

  function onClickPhone() {
    dispatch(
      uiActions.openPhoneScreen({
        patientId: patient?.id,
        number: patient?.phone,
        appointmentId: appointment?.id,
      }),
    );
    startQueueAppointment();
  }

  function onClickChat() {
    openChat(true, mainPatient.id);
  }

  function changeDefaultSize(size) {
    if (size !== undefined) setDefaultSize(`calc(min(max(${size}px, 10vw), 90vw)`);
  }

  function onClickInsights() {
    dispatch(
      uiActions.addAssistant({
        setActive: true,
        firstMessage: {
          type: 'promptData',
          savedPromptData: {
            name: 'Patient Analysis',
            savedPromptRequest: {
              fields: [],
              patient_id: patient.id,
            },
          },
        },
        subject: PatientUtils.getName(patient, undefined, false, false) + ' Analysis',
      }),
    );
  }

  const isTelemedicine = AppointmentUtils.isTelemedicineAppointment(appointment);

  const shouldShowUserIcons = !isPhone || showUserIcons;

  return (
    <CPPContext.Provider value={cppContextState}>
      <SplitPanel
        onChangeSize={(size) => changeDefaultSize(size)}
        right={
          pinnedDocuments && pinnedDocuments.length > 0 ? (
            <CPPSplitPanel pinned={pinnedDocuments} patient={patient} bubble={patientBubble} />
          ) : null
        }
        defaultSize={defaultSize}
        key={patientId}
      >
        <div className={className}>
          <div className="chat-cpp-main-section">
            <div
              className={`top-row mobile-top-row main-top-row ${
                shouldShowUserIcons ? 'showIcons' : ''
              }`}
            >
              <div className="info-row-wrapper">
                <PatientInfoRow patient={patient} />
                {isPhone && (
                  <div
                    className="expand-user-icons"
                    onClick={() => setShowUserIcons((prev) => !prev)}
                  >
                    <Icon
                      src={['fas', 'ellipsis']}
                      className="white"
                      height="35px"
                      alt="Show User Icons"
                      showTooltip={true}
                      tooltipPlace="bottom"
                    />
                  </div>
                )}
              </div>
              <div className="user-icons">
                {allowClinicTranscription && <TranscriptionIcon appointment={appointment} />}
                <SearchIcon search={search} setSearch={setSearch} />
                {!isAdmin && (
                  <div className="icon-bubble" onClick={onClickInsights}>
                    <Icon
                      src={['fas', 'microchip-ai']}
                      className="secondary"
                      height="20px"
                      alt="Carey Patient Insights (AI Assistant)"
                      showTooltip={true}
                      tooltipPlace="bottom"
                    />
                  </div>
                )}
                {!isPhlebotomist && (
                  <>
                    {isTelemedicine && (
                      <div
                        className="icon-bubble"
                        onClick={() => startVideoCall(patientBubble?.appointment_id)}
                      >
                        <Icon
                          src={['fas', 'video']}
                          className="secondary"
                          height="20px"
                          alt="Start Video Call"
                          showTooltip={true}
                          tooltipPlace="bottom"
                        />
                      </div>
                    )}
                  </>
                )}
                <div className="icon-bubble" onClick={onClickPhone}>
                  <Icon
                    src={['fas', 'phone']}
                    className="secondary"
                    height="20px"
                    alt="Start Phone Call"
                    showTooltip={true}
                    tooltipPlace="bottom"
                  />
                </div>
                <div className="icon-bubble" onClick={onClickChat}>
                  <Icon
                    src={['fas', 'message']}
                    className="secondary"
                    height="20px"
                    loading={loadingChatIcon}
                    alt="Start Chat"
                    showTooltip={true}
                    tooltipPlace="bottom"
                  />
                </div>
                {patientBubble?.appointment_type === AppointmentUtils.getAppointmentTypes().home ? (
                  <div
                    className="icon-bubble"
                    onClick={() => {
                      toggleAddress(true);
                    }}
                  >
                    <Icon
                      src={['fas', 'location-dot']}
                      className="secondary"
                      height="20px"
                      alt="Show Address"
                      showTooltip={true}
                      tooltipPlace="bottom"
                    />
                  </div>
                ) : null}
                <div
                  className="icon-menu"
                  onClick={() => {
                    toggleResources(true);
                  }}
                >
                  <Icon
                    src={['fas', 'ellipsis-vertical']}
                    className="white"
                    height="35px"
                    alt="Utilities"
                    showTooltip={true}
                    tooltipPlace="bottom"
                  />
                </div>
              </div>
            </div>
            <div className="chat-cpp-content">
              <CPP
                patient={patient}
                bubble={patientBubble}
                showTimeBar={true}
                search={search}
                setSearch={setSearch}
                onArchiveChat={archiveChat}
                queueElement={queueElement}
                defaultHidePanel={defaultHidePanel}
              />
            </div>
          </div>
          <div className={chatClassNames}>
            {showResources && (
              <ResourcesPanel
                onClose={() => {
                  toggleResources(false);
                }}
                bubble={patientBubble}
                patient={mainPatient}
              />
            )}
            {showChat && (
              <ChatRoomWindow
                bubble={patientBubble}
                patient={mainPatient}
                onClose={closeChat}
                disableSMS={false}
                hideTopBar={true}
                allowAssistant={true}
                renderTopBar={() => (
                  <PhysicianTopBar
                    patient={patient}
                    archiveChat={onClickedToArchive}
                    onClose={closeChat}
                  />
                )}
              />
            )}
            {showAddress && (
              <AddressSidePanel appointment={appointment} onClose={() => toggleAddress(false)} />
            )}
          </div>
        </div>
      </SplitPanel>
    </CPPContext.Provider>
  );

  function onClickedToArchive() {
    if (patientBubble) {
      return archiveChat(undefined, patientBubble.appointment_id);
    }
  }

  function archiveChat(channelId, appointmentId, updateStatus = undefined) {
    function goToHome() {
      nav.push(RouteUtils.routes().root);
    }

    function removeBubbleWrapper() {
      closeChat();
      if (patientBubble) {
        goToHome();
      }
    }

    async function archiveChat() {
      return props.onArchive(undefined, appointmentId, patientBubble?.room_name);
    }

    let hasChat = !!patientBubble.room_name;
    let isJustChat = hasChat && !appointmentId;
    let closingAppointment = !!updateStatus && appointmentId;

    if (closingAppointment) {
      dispatch(
        api.Appointments.updateAppointmentStatus({
          id: appointmentId,
          status: updateStatus,
        }),
      ).then(async (result) => {
        if (result) {
          if (hasChat) {
            archiveChat().finally(() => {
              removeBubbleWrapper();
            });
          } else {
            removeBubbleWrapper();
          }
        }
      });
    } else if (hasChat) {
      closeChat();
      archiveChat().then((result) => {
        if (result && isJustChat) {
          removeBubbleWrapper();
        }
      });
    }
  }
};

const TranscriptionIcon = ({ appointment }) => {
  const dispatch = useAppDispatch();
  const transcriptionUI = useAppSelector((state) => state.ui.transcription);

  function onClick() {
    if (!transcriptionUI) {
      dispatch(
        uiActions.openTranscriptionScreen({
          patientId: appointment.patient_id,
          appointmentId: appointment.id,
          status: TRANSCRIPTION_STATUS.inactive,
          minimized: false,
        }),
      );
    } else {
      dispatch(uiActions.toggleTranscriptionMinimized());
    }
  }

  return (
    <>
      <div
        className="transcription-icon icon-bubble"
        data-status={transcriptionUI?.status}
        onClick={onClick}
      >
        <Icon
          width="20px"
          src="microphone"
          alt="Start In-Clinic Transcription"
          className="secondary"
          showTooltip={true}
          tooltipPlace="bottom"
        />
      </div>
    </>
  );
};

const SearchIcon = React.memo(({ search, setSearch }) => {
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    if (search) setShowSearch(true);
  }, [search]);

  let bubbleClassName = 'icon-bubble';
  if (showSearch) bubbleClassName += ' grey';

  function toggleSearch() {
    setSearch('');
    setShowSearch(!showSearch);
  }

  function onChangeSearch(event) {
    setSearch(event.target.value);
  }

  const MAIN_STYLE = { display: 'inherit' };
  const TEXT_STYLE = { width: '100%' };

  return (
    <div style={MAIN_STYLE}>
      <div className={bubbleClassName} onClick={toggleSearch}>
        <Icon
          width="20px"
          src={['fas', 'magnifying-glass']}
          alt="Search CPP"
          className="secondary"
          showTooltip={true}
          tooltipPlace="bottom"
        />

        {!!showSearch && (
          <div className="search-bar">
            <div className="marker" />
            <DefaultTextField
              value={search}
              autoFocus={true}
              name="search"
              placeholder="Search"
              onChange={onChangeSearch}
              style={TEXT_STYLE}
            />
          </div>
        )}
      </div>
    </div>
  );
});

CPPChat.propTypes = {
  bubble: PropTypes.object,
  patient: PropTypes.object,
  onArchive: PropTypes.func,
  videoScreen: PropTypes.element,
};

export default CPPChat;
